import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Download, Sparkles, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { DatePicker } from "@/components/ui/DatePicker";
import { Spinner } from "@/components/ui/Spinner";
import {
  useReportMutations,
  useReportSchedule,
  useReports,
  useTaskPolling,
  useUpdateConnection,
} from "../hooks";
import type {
  PowerProject, ReportSchedule, ReportSlot, ReportSlotState, ZEDocFormat, ZEFormMetric,
  ZEReport, ZEReportDraft,
} from "../types";
import {
  REPORTING_MODE_LABEL, ZE_DOC_FORMAT_LABEL, ZE_FORM_METRIC_GAPS, type ReportingMode,
} from "../types";
import { fmtDate, fmtDateTime } from "../utils";
import { AnnexRequests } from "./AnnexRequests";
import { AttachmentLinks, EmptyState, MultiFileInput, SectionCard } from "./ui";

/** Domyślny zestaw postaci dokumentu. ENERGA czyta drogą oficjalną wyłącznie swoją
 *  formatkę, więc na jej projektach proponujemy oba dokumenty; gdziekolwiek indziej
 *  zaczynamy od pisma. Koordynator i tak może zaznaczyć, co chce. */
const defaultFormats = (operator: string): ZEDocFormat[] =>
  operator === "energa" ? ["letter", "eop_form"] : ["letter"];

/** Rubryki metryki, których w projekcie nie ma. Formularz i tak powstaje — z pustą
 *  rubryką — ale koordynator ma wiedzieć, co dopisać i pod jaką dokładnie etykietą,
 *  bo generator czyta te kolumny po nazwie. */
function MetricGaps({ metric }: { metric: ZEReportDraft["form_metric"] }) {
  const gaps = ZE_FORM_METRIC_GAPS.filter((g) => !(metric as ZEFormMetric)?.[g.key]);
  if (!gaps.length) return null;
  return (
    <div className="space-y-0.5">
      <p className="text-xs text-[var(--color-primary)]">
        Formatka wyszła z pustymi rubrykami — uzupełnij je raz, a kolejne raporty i wnioski
        o aneks wypełnią się same:
      </p>
      <ul className="space-y-0.5">
        {gaps.map((g) => (
          <li key={g.key} className="text-xs text-[var(--color-text-secondary)]">
            <span className="font-medium">{g.label}</span> — {g.where}
          </li>
        ))}
      </ul>
    </div>
  );
}

function FormatPicker({
  value,
  onChange,
}: {
  value: ZEDocFormat[];
  onChange: (v: ZEDocFormat[]) => void;
}) {
  const toggle = (f: ZEDocFormat) =>
    onChange(value.includes(f) ? value.filter((x) => x !== f) : [...value, f]);
  return (
    <div className="flex flex-wrap items-center gap-3">
      {(Object.keys(ZE_DOC_FORMAT_LABEL) as ZEDocFormat[]).map((f) => (
        <label key={f} className="flex items-center gap-1.5 text-xs">
          <input type="checkbox" checked={value.includes(f)} onChange={() => toggle(f)} />
          {ZE_DOC_FORMAT_LABEL[f]}
        </label>
      ))}
    </div>
  );
}

const SLOT_META: Record<ReportSlotState, { color: "green" | "red" | "yellow" | "gray"; label: string }> = {
  done: { color: "green", label: "Wysłany" },
  overdue: { color: "red", label: "Po terminie" },
  due_soon: { color: "yellow", label: "Zbliża się termin" },
  planned: { color: "gray", label: "Zaplanowany" },
};

function daysLabel(slot: ReportSlot): string {
  if (slot.state === "done") return "—";
  if (slot.days_left < 0) return `${Math.abs(slot.days_left)} dni po terminie`;
  if (slot.days_left === 0) return "dzisiaj";
  if (slot.days_left === 1) return "jutro";
  return `za ${slot.days_left} dni`;
}

/** Nagłówek: czy projekt w ogóle raportuje, dlaczego i kiedy najbliższy termin. */
function ScheduleSummary({ schedule }: { schedule: ReportSchedule }) {
  const next = schedule.next_slot;
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Badge color={schedule.required ? "blue" : "gray"}>
          {schedule.required ? "Raportowanie wymagane" : "Raportowanie niewymagane"}
        </Badge>
        <Badge color={schedule.voltage === "SN" ? "purple" : "gray"}>
          Napięcie: {schedule.voltage ?? "nieustalone"}
        </Badge>
        {schedule.required && <Badge color="gray">Co {schedule.interval_months} mies.</Badge>}
        {schedule.overdue_count > 0 && (
          <Badge color="red">Zaległe raporty: {schedule.overdue_count}</Badge>
        )}
        {schedule.closed && <Badge color="black">Temat zamknięty</Badge>}
      </div>
      <p className="text-xs text-[var(--color-text-secondary)]">{schedule.reason}</p>
      {schedule.required && (
        <p className="text-xs text-[var(--color-text-secondary)]">
          Start raportowania:{" "}
          <span className="font-mono">{fmtDate(schedule.start_date)}</span>{" "}
          {schedule.start_source === "contract"
            ? "(data zawarcia umowy + 6 tygodni)"
            : schedule.start_source === "override"
              ? "(ustawiony ręcznie)"
              : ""}
          {next && (
            <>
              {" · najbliższy termin: "}
              <span className="font-mono">{fmtDate(next.due_date)}</span> ({daysLabel(next)})
            </>
          )}
        </p>
      )}
      {schedule.active && schedule.overdue_count > 0 && (
        <p className="text-xs text-[var(--color-primary)]">
          Ponaglenie o najstarszy zaległy raport idzie codziennie do dyrektorów technicznych
          i kierowników przedsięwzięcia — ucichnie dopiero po oznaczeniu raportu jako wysłanego.
        </p>
      )}
      {schedule.required && !schedule.active && !schedule.closed && (
        <p className="text-xs text-[var(--color-primary)]">
          Przypomnienia nie działają — uzupełnij datę zawarcia umowy albo ustaw start ręcznie.
        </p>
      )}
    </div>
  );
}

/** Ustawienia raportowania per projekt — przełącznik zamiast wpisu w konfiguracji. */
function ReportingSettings({ connection }: { connection: PowerProject }) {
  const update = useUpdateConnection(connection.id);
  const [open, setOpen] = useState(false);
  return (
    <SectionCard
      title="Ustawienia raportowania"
      action={
        <Button size="sm" variant="ghost" onClick={() => setOpen((v) => !v)}>
          {open ? "Zwiń" : "Zmień"}
        </Button>
      }
    >
      {!open ? (
        <p className="text-xs text-[var(--color-text-secondary)]">
          Tryb: {REPORTING_MODE_LABEL[connection.reporting_mode]}. Domyślnie napięcie i cykl
          liczy system z narysowanej trasy (SN → co 2 miesiące, nN → bez raportów).
        </p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-3">
          <label className="space-y-1">
            <span className="section-label block">Raportowanie</span>
            <select
              className="input-base w-full"
              value={connection.reporting_mode}
              onChange={(e) => update.mutate({ reporting_mode: e.target.value as ReportingMode })}
            >
              {(Object.keys(REPORTING_MODE_LABEL) as ReportingMode[]).map((m) => (
                <option key={m} value={m}>
                  {REPORTING_MODE_LABEL[m]}
                </option>
              ))}
            </select>
          </label>
          <label className="space-y-1">
            <span className="section-label block">Interwał [mies.]</span>
            <input
              type="number"
              min={1}
              max={24}
              className="input-base w-full"
              defaultValue={connection.reporting_interval_months ?? ""}
              placeholder="2"
              onBlur={(e) =>
                update.mutate({
                  reporting_interval_months: e.target.value ? Number(e.target.value) : null,
                })
              }
            />
          </label>
          <label className="space-y-1">
            <span className="section-label block">Start raportowania</span>
            <DatePicker
              value={connection.reporting_start_override ?? ""}
              onChange={(v) => update.mutate({ reporting_start_override: v || null })}
            />
          </label>
          <label className="space-y-1 sm:col-span-3">
            <span className="section-label block">Przewidywany termin zakończenia zadania</span>
            <DatePicker
              value={connection.expected_completion_date ?? ""}
              onChange={(v) => update.mutate({ expected_completion_date: v || null })}
            />
            <span className="block text-xs text-[var(--color-text-secondary)]">
              Rubryka z formatki operatora. Puste = system oszacuje ją z planu prac i oznaczy
              w dokumencie jako „(szac.)". Wpisana data idzie do raportu jako deklaracja —
              operator z niej rozlicza.
            </span>
          </label>
        </div>
      )}
    </SectionCard>
  );
}

function AddReportForm({ connection, schedule }: { connection: string; schedule?: ReportSchedule }) {
  const m = useReportMutations(connection);
  const open = (schedule?.slots ?? []).filter((s) => s.state !== "done");
  const [title, setTitle] = useState("");
  const [periodDue, setPeriodDue] = useState<string>(open[0]?.due_date ?? "");
  const [notes, setNotes] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    try {
      await m.create.mutateAsync({
        connection,
        title: title.trim() || `Raport ${fmtDate(periodDue || null)}`,
        period_due: periodDue || null,
        notes,
        files,
      });
      setTitle("");
      setNotes("");
      setFiles([]);
    } catch {
      setError("Nie udało się dodać raportu.");
    }
  };

  return (
    <SectionCard title="Dodaj raport">
      <div className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="space-y-1">
            <span className="section-label block">Tytuł</span>
            <input
              className="input-base w-full"
              value={title}
              placeholder="np. Raport za styczeń–luty"
              onChange={(e) => setTitle(e.target.value)}
            />
          </label>
          <label className="space-y-1">
            <span className="section-label block">Termin z harmonogramu</span>
            <select
              className="input-base w-full"
              value={periodDue}
              onChange={(e) => setPeriodDue(e.target.value)}
            >
              <option value="">Poza harmonogramem (doraźny)</option>
              {open.map((s) => (
                <option key={s.due_date} value={s.due_date}>
                  Raport nr {s.sequence_no} — {fmtDate(s.due_date)}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="space-y-1 block">
          <span className="section-label block">Uwagi</span>
          <textarea
            className="input-base w-full"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </label>
        <MultiFileInput files={files} onChange={setFiles} label="Pliki raportu" />
        {error && <p className="text-xs text-[var(--color-primary)]">{error}</p>}
        <Button size="sm" onClick={submit} loading={m.create.isPending}>
          Dodaj raport
        </Button>
      </div>
    </SectionCard>
  );
}

/** Szkic pisma napisany przez ARON: stan, plik .docx do edycji i podgląd treści. */
function ReportDraftPanel({
  report,
  connection,
  operator,
  onTask,
  busy,
}: {
  report: ZEReport;
  connection: string;
  operator: string;
  onTask: (taskId: string) => void;
  busy: boolean;
}) {
  const m = useReportMutations(connection);
  const [open, setOpen] = useState(false);
  // Po regeneracji trzymamy się tego, co wygenerowano ostatnio — koordynator, który
  // raz zdecydował „obie postaci", nie chce tego klikać przy każdym raporcie.
  const [formats, setFormats] = useState<ZEDocFormat[]>(
    report.draft_formats?.length ? report.draft_formats : defaultFormats(operator),
  );
  const draft = report.draft;
  // „W trakcie" z bazy tylko POKAZUJEMY. Blokuje wyłącznie zadanie z tej sesji —
  // inaczej generacja ubita deployem zostawiłaby przycisk martwy na zawsze.
  const pending = report.draft_status === "pending";
  const generate = () =>
    m.generateDraft.mutate({ id: report.id, formats }, { onSuccess: (r) => onTask(r.task_id) });

  return (
    <div className="border border-[var(--color-border-muted)] bg-[var(--color-bg-elevated)] p-2 space-y-1.5">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="font-medium">Dokumenty raportu (ARON)</span>
        {report.draft_status === "success" && draft && (
          <Badge color={draft.ai ? "green" : "yellow"}>
            {draft.ai ? "Napisane przez ARON" : "Zestawienie faktów — do przeredagowania"}
          </Badge>
        )}
        {report.draft_status === "failure" && <Badge color="red">Błąd generowania</Badge>}
        {pending && <Badge color="gray">Generowanie…</Badge>}
        <div className="ml-auto flex items-center gap-2">
          {report.draft_file?.url && (
            <a
              href={report.draft_file.url}
              download
              className="text-[var(--color-primary)] hover:underline inline-flex items-center gap-1"
            >
              <Download size={12} /> pismo .docx
            </a>
          )}
          {report.draft_form_file?.url && (
            <a
              href={report.draft_form_file.url}
              download
              className="text-[var(--color-primary)] hover:underline inline-flex items-center gap-1"
            >
              <Download size={12} /> formatka operatora (Zał. 4 OWU) .docx
            </a>
          )}
          {draft && (draft.sections.length > 0 || draft.form_rows.length > 0) ? (
            <button
              className="text-[var(--color-primary)] hover:underline"
              onClick={() => setOpen((v) => !v)}
            >
              {open ? "Ukryj treść" : "Podgląd treści"}
            </button>
          ) : null}
          <Button size="sm" variant="secondary" loading={busy} onClick={generate} disabled={!formats.length}>
            <Sparkles size={13} />
            {report.draft_status === "success" ? "Generuj ponownie" : "Przygotuj treść"}
          </Button>
        </div>
      </div>
      <FormatPicker value={formats} onChange={setFormats} />

      {report.draft_status === "success" && draft && (
        <p className="text-xs text-[var(--color-text-secondary)]">
          Okres {draft.period.from_display ?? "?"}–{draft.period.to_display ?? "?"} ·{" "}
          {draft.facts_total} zdarzeń · wygenerowano {fmtDateTime(report.draft_generated_at)}
        </p>
      )}
      {report.draft_status === "failure" && report.draft_error && (
        <p className="text-xs text-[var(--color-primary)]">{report.draft_error}</p>
      )}
      {report.draft_status === "none" && !pending && (
        <p className="text-xs text-[var(--color-text-secondary)]">
          ARON zbierze zdarzenia z okresu (wysłane wnioski, uzgodnienia, decyzje, problemy)
          i złoży z nich pismo do operatora w pliku .docx — do poprawienia przed wysyłką.
        </p>
      )}
      {open && draft && (
        <div className="space-y-2 pt-1">
          {draft.sections.map((s) => (
            <div key={s.month_key}>
              <p className="text-xs font-medium">{s.heading}:</p>
              <p className="text-xs text-[var(--color-text-secondary)] whitespace-pre-wrap">{s.text}</p>
            </div>
          ))}
          {draft.closing && (
            <p className="text-xs text-[var(--color-text-secondary)] whitespace-pre-wrap">
              {draft.closing}
            </p>
          )}
          {report.draft_form_file && <MetricGaps metric={draft.form_metric} />}
          {draft.form_rows.length > 0 && (
            <div className="space-y-1 pt-1">
              <p className="section-label">Formatka — czynności zrealizowane</p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left section-label">
                      <th className="py-1 pr-2">Data</th>
                      <th className="py-1 pr-2">Czynność</th>
                      <th className="py-1 pr-2">Uwagi</th>
                      <th className="py-1">Załączniki</th>
                    </tr>
                  </thead>
                  <tbody>
                    {draft.form_rows.map((row, i) => (
                      <tr key={`${row.date_display}-${i}`} className="border-t border-[var(--color-border-muted)]">
                        <td className="py-1 pr-2 font-mono whitespace-nowrap">{row.date_display}</td>
                        <td className="py-1 pr-2">{row.action}</td>
                        <td className="py-1 pr-2 text-[var(--color-text-secondary)]">{row.note}</td>
                        <td className="py-1 text-[var(--color-text-secondary)]">{row.attachment || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {draft.form_planned.length > 0 && (
            <div className="space-y-1 pt-1">
              <p className="section-label">Formatka — plan dalszych prac</p>
              <ul className="space-y-0.5">
                {draft.form_planned.map((row) => (
                  <li key={`${row.date}-${row.text}`} className="text-xs text-[var(--color-text-secondary)]">
                    <span className="font-mono">{row.date_display}</span>
                    {row.source === "estimate" ? "*" : ""} — {row.text}
                  </li>
                ))}
              </ul>
              {draft.form_planned.some((r) => r.source === "estimate") && (
                <p className="text-xs text-[var(--color-text-muted)]">
                  * termin policzony przez system z harmonogramu kamieni — potwierdź go
                  przed wysłaniem, bo operator rozlicza nas z terminów w tej tabeli.
                </p>
              )}
            </div>
          )}
          {(draft.form_planned.length > 0 || draft.form_rows.length > 0) &&
            (draft.form_planned.some((r) => r.source === "estimate") ||
              draft.form_metric?.expected_completion_source === "plan") && (
              <p className="text-xs text-[var(--color-text-muted)]">
                Przewidywany termin zakończenia wpiszesz raz w „Ustawieniach raportowania" —
                wtedy przestanie być szacunkiem systemu.
              </p>
            )}
          {!!draft.form_attachments?.length && (
            <div className="space-y-1 pt-1">
              <p className="section-label">Do dopięcia do maila ({draft.form_attachments.length})</p>
              <ul className="space-y-0.5">
                {draft.form_attachments.map((row) => (
                  <li key={row.file} className="text-xs text-[var(--color-text-secondary)]">
                    <span className="font-mono">{row.file}</span> — {row.document}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-[var(--color-text-muted)]">
                Ta sama lista jest na końcu pliku formatki. Skanów wysłanych przy
                wcześniejszych raportach nie powtarzamy — w tabeli stoi przy nich
                „załączono do raportu nr …".
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ReportRow({
  report,
  connection,
  operator,
  onTask,
  busy,
}: {
  report: ZEReport;
  connection: string;
  operator: string;
  onTask: (taskId: string) => void;
  busy: boolean;
}) {
  const m = useReportMutations(connection);
  const [sentAt, setSentAt] = useState<string>("");
  const sent = report.status === "sent";
  return (
    <div className="border-b border-[var(--color-border-muted)] last:border-b-0 py-2.5 space-y-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <Badge color={sent ? "green" : "gray"}>{report.status_display}</Badge>
        <span className="font-medium">{report.title || "Raport"}</span>
        <span className="text-xs text-[var(--color-text-muted)] font-mono">
          {report.period_due ? `termin ${fmtDate(report.period_due)}` : "poza harmonogramem"}
        </span>
        <div className="ml-auto flex items-center gap-2">
          {sent ? (
            <Button size="sm" variant="ghost" onClick={() => m.unmarkSent.mutate(report.id)}>
              Cofnij wysłanie
            </Button>
          ) : (
            <>
              <DatePicker value={sentAt} onChange={setSentAt} />
              <Button
                size="sm"
                variant="secondary"
                loading={m.markSent.isPending}
                onClick={() => m.markSent.mutate({ id: report.id, sent_at: sentAt || undefined })}
              >
                Wysłane do ZE
              </Button>
            </>
          )}
          <Button
            size="sm"
            variant="ghost"
            title="Usuń raport"
            onClick={() => {
              if (window.confirm("Usunąć ten raport?")) m.remove.mutate(report.id);
            }}
          >
            <Trash2 size={14} />
          </Button>
        </div>
      </div>
      {sent && (
        <p className="text-xs text-[var(--color-text-secondary)]">
          Wysłany {fmtDate(report.sent_at)}
          {report.sent_by_name ? ` — ${report.sent_by_name}` : ""}
        </p>
      )}
      {report.notes && (
        <p className="text-xs text-[var(--color-text-secondary)] whitespace-pre-wrap">{report.notes}</p>
      )}
      <AttachmentLinks files={report.files} />
      <ReportDraftPanel
        report={report}
        connection={connection}
        operator={operator}
        onTask={onTask}
        busy={busy}
      />
    </div>
  );
}

function ScheduleTable({
  schedule,
  reports,
  connection,
  operator,
  onTask,
  busy,
}: {
  schedule: ReportSchedule;
  reports: ZEReport[];
  connection: string;
  operator: string;
  onTask: (taskId: string) => void;
  busy: boolean;
}) {
  const byId = useMemo(() => new Map(reports.map((r) => [r.id, r])), [reports]);
  const m = useReportMutations(connection);
  const [pendingSlot, setPendingSlot] = useState<string | null>(null);
  // Termin z harmonogramu jest klikalny wprost: zakłada raport na TEN termin i od razu
  // każe ARON-owi przygotować dokumenty — koordynator nie musi szukać przycisku niżej.
  const prepare = async (slot: ReportSlot) => {
    setPendingSlot(slot.due_date);
    try {
      const report = await m.create.mutateAsync({
        connection,
        title: `Raport nr ${slot.sequence_no} na termin ${fmtDate(slot.due_date)}`,
        period_due: slot.due_date,
        notes: "",
        files: [],
      });
      const task = await m.generateDraft.mutateAsync({ id: report.id, formats: defaultFormats(operator) });
      onTask(task.task_id);
    } finally {
      setPendingSlot(null);
    }
  };
  if (!schedule.slots.length) {
    return <EmptyState>Brak wyznaczonych terminów raportów.</EmptyState>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left section-label">
            <th className="py-1.5 pr-3">Nr</th>
            <th className="py-1.5 pr-3">Termin</th>
            <th className="py-1.5 pr-3">Status</th>
            <th className="py-1.5 pr-3">Pozostało</th>
            <th className="py-1.5 pr-3">Raport</th>
            <th className="py-1.5"></th>
          </tr>
        </thead>
        <tbody>
          {schedule.slots.map((slot) => {
            const meta = SLOT_META[slot.state];
            const linked = slot.report_ids.map((id) => byId.get(id)).filter(Boolean) as ZEReport[];
            return (
              <tr key={slot.due_date} className="border-t border-[var(--color-border-muted)]">
                <td className="py-1.5 pr-3 font-mono text-[var(--color-text-muted)]">{slot.sequence_no}</td>
                <td className="py-1.5 pr-3 font-mono">{fmtDate(slot.due_date)}</td>
                <td className="py-1.5 pr-3">
                  <Badge color={meta.color}>{meta.label}</Badge>
                </td>
                <td className="py-1.5 pr-3 text-xs text-[var(--color-text-secondary)]">{daysLabel(slot)}</td>
                <td className="py-1.5 pr-3 text-xs text-[var(--color-text-secondary)]">
                  {linked.length ? linked.map((r) => r.title || "Raport").join(", ") : "—"}
                </td>
                <td className="py-1.5">
                  {slot.state !== "done" && !linked.length && (
                    <Button
                      size="sm"
                      variant="ghost"
                      loading={pendingSlot === slot.due_date}
                      disabled={busy || (pendingSlot !== null && pendingSlot !== slot.due_date)}
                      onClick={() => prepare(slot)}
                      title="Załóż raport na ten termin i przygotuj pismo / formatkę operatora"
                    >
                      <Sparkles size={12} />
                      Przygotuj raport
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** „Raz na 2 miesiące jednym kliknięciem": zakłada raport na najbliższy otwarty
 *  termin i od razu każe ARON-owi napisać jego treść. */
function PrepareNextReport({
  connection,
  operator,
  schedule,
  onTask,
  busy,
}: {
  connection: string;
  operator: string;
  schedule?: ReportSchedule;
  onTask: (taskId: string) => void;
  busy: boolean;
}) {
  const m = useReportMutations(connection);
  const [error, setError] = useState<string | null>(null);
  const [formats, setFormats] = useState<ZEDocFormat[]>(defaultFormats(operator));
  const next = (schedule?.slots ?? []).find((s) => s.state !== "done");
  // Świadomie BEZ bramki `schedule.required`. Obowiązek cyklicznego raportowania (trasa
  // z elementem SN) i potrzeba JEDNEGO raportu to dwie różne rzeczy: raport z postępu
  // prac jest wymaganym załącznikiem wniosku o aneks, a operator potrafi go zażądać na
  // każdym zadaniu. Na produkcji 198 z 210 projektów ZE nie ma cyklu — i to właśnie na
  // nich koordynatorzy pisali formatkę ręcznie, bo przycisku nie było widać.

  const run = async () => {
    setError(null);
    try {
      const report = await m.create.mutateAsync({
        connection,
        title: next ? `Raport na termin ${fmtDate(next.due_date)}` : "Raport doraźny",
        period_due: next?.due_date ?? null,
        notes: "",
        files: [],
      });
      const task = await m.generateDraft.mutateAsync({ id: report.id, formats });
      onTask(task.task_id);
    } catch {
      setError("Nie udało się przygotować raportu.");
    }
  };

  return (
    <SectionCard title="Przygotuj raport o postępie prac projektowych">
      <div className="space-y-2">
        <p className="text-xs text-[var(--color-text-secondary)]">
          ARON zbierze, co działo się na projekcie od poprzedniego raportu (wysłane wnioski,
          uzgodnienia, decyzje, zgody, problemy) i złoży z tego dokument w pliku .docx —
          do sprawdzenia i poprawienia przed wysłaniem. Pismo nadaje się do maila (tak
          raportujemy do TAURON); formatka operatora to wypełniony oryginalny wzór ENERGA
          „Załącznik nr 4 do OWU”, którego operator wymaga drogą oficjalną (i jako
          załącznik do wniosku o aneks). Uwaga: „formatka” bez dopisku znaczy w systemie
          wydruk mapy — tu chodzi o raport do operatora.
        </p>
        {schedule && !schedule.required && (
          <p className="text-xs text-[var(--color-text-secondary)]">
            Ten projekt nie ma cyklicznego obowiązku raportowania ({schedule.reason}), ale
            raport doraźny przygotujesz tu w każdej chwili — np. jako załącznik do wniosku
            o aneks.
          </p>
        )}
        <FormatPicker value={formats} onChange={setFormats} />
        {error && <p className="text-xs text-[var(--color-primary)]">{error}</p>}
        <Button
          size="sm"
          loading={busy || m.create.isPending}
          disabled={!formats.length}
          onClick={run}
        >
          <Sparkles size={14} />
          {next
            ? `Przygotuj raport na termin ${fmtDate(next.due_date)}`
            : "Przygotuj raport doraźny"}
        </Button>
      </div>
    </SectionCard>
  );
}

export function ReportsTab({ connection }: { connection: PowerProject }) {
  const qc = useQueryClient();
  const { data: schedule, isLoading: loadingSchedule } = useReportSchedule(connection.id);
  const { data: reports, isLoading: loadingReports } = useReports(connection.id);
  const [taskId, setTaskId] = useState<string | null>(null);
  const task = useTaskPolling(taskId);
  const taskStatus = task.data?.status;
  const busy = !!taskId && !["success", "failure", "cancelled"].includes(taskStatus ?? "");

  // Szkic siedzi na samym raporcie, więc po zakończeniu zadania odświeżamy listę.
  useEffect(() => {
    if (taskId && taskStatus && ["success", "failure", "cancelled"].includes(taskStatus)) {
      qc.invalidateQueries({ queryKey: ["pp", "reports", connection.id] });
      setTaskId(null);
    }
  }, [taskId, taskStatus, qc, connection.id]);

  if (loadingSchedule || loadingReports) {
    return (
      <div className="py-8 flex justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-4">
      {schedule && (
        <SectionCard title="Raporty okresowe do ZE">
          <ScheduleSummary schedule={schedule} />
        </SectionCard>
      )}
      <PrepareNextReport
        connection={connection.id}
        operator={connection.operator}
        schedule={schedule}
        onTask={setTaskId}
        busy={busy}
      />
      <ReportingSettings connection={connection} />
      {/* Harmonogram pokazujemy tylko, gdy projekt faktycznie raportuje — na nN
          nie ma terminów, a pusta tabela sugerowałaby, że coś się nie policzyło. */}
      {schedule?.required && (
        <SectionCard title="Harmonogram terminów">
          <ScheduleTable
            schedule={schedule}
            reports={reports ?? []}
            connection={connection.id}
            operator={connection.operator}
            onTask={setTaskId}
            busy={busy}
          />
        </SectionCard>
      )}
      <AddReportForm connection={connection.id} schedule={schedule} />
      <SectionCard title={`Raporty (${reports?.length ?? 0})`}>
        {reports?.length ? (
          reports.map((r) => (
            <ReportRow
              key={r.id}
              report={r}
              connection={connection.id}
              operator={connection.operator}
              onTask={setTaskId}
              busy={busy && r.draft_status === "pending"}
            />
          ))
        ) : (
          <EmptyState>Brak raportów. Dodaj pierwszy powyżej.</EmptyState>
        )}
      </SectionCard>
      <AnnexRequests connection={connection} />
    </div>
  );
}
